// Portfolio Administration v1.0 PR 5 — TanStack Query hooks for the
// cross-portfolio Vendors + Local Enforcers registry pages. Detail-page
// hooks live alongside list-page hooks here so consumers can import a
// single module per entity. Mirrors the @/hooks/portfolioAdmin/usePr4b
// conventions: stable query-key factory + mutation invalidation.

import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { vendorsApi } from "@/api/portfolioAdmin/client";
import { vendorBillingKeys } from "@/hooks/portfolioAdmin/useVendorBilling";
import {
  enforcerEngagementsApi,
  localEnforcersApi,
} from "@/api/portfolioAdmin/pr4bClient";
import type {
  ArchiveVendorRequest,
  CreateVendorContractTermRequest,
  CreateVendorRequest,
  UpdateVendorProfileRequest,
  VendorArchiveImpactDto,
  ConfirmVendorContractTermProposalRequest,
  VendorContractTermDto,
  VendorContractTermProposalDto,
  VendorDto,
  VendorLoanAssignmentDto,
  VendorPortalAccountDto,
  VendorSearchParams,
} from "@/api/portfolioAdmin/types";
import type {
  CreateLocalEnforcerRequest,
  EnforcerEngagementDto,
  LocalEnforcerDetailDto,
  LocalEnforcerDto,
  LocalEnforcerPerformanceReviewRequest,
  LocalEnforcerSearchParams,
  QualifyLocalEnforcerRequest,
  UpdateLocalEnforcerStatusRequest,
} from "@/api/portfolioAdmin/pr4bTypes";

// ============================== query keys ============================

export const registryKeys = {
  all: ["portfolio-admin", "registries"] as const,
  /** Prefix covering every vendor read (list/detail/assignments/…). */
  vendorsPrefix: ["portfolio-admin", "vendors"] as const,
  vendorList: (params?: VendorSearchParams) =>
    [
      "portfolio-admin",
      "vendors",
      "list",
      params?.search ?? null,
      (params?.types ?? []).join(",") || null,
      (params?.statuses ?? []).join(",") || null,
      params?.includeArchived ? "archived" : null,
    ] as const,
  vendor: (vendorId: string) =>
    ["portfolio-admin", "vendors", "detail", vendorId] as const,
  vendorAssignments: (vendorId: string) =>
    ["portfolio-admin", "vendors", "assignments", vendorId] as const,
  vendorPortalAccount: (vendorId: string) =>
    ["portfolio-admin", "vendors", "portal-account", vendorId] as const,
  vendorArchiveImpact: (vendorId: string) =>
    ["portfolio-admin", "vendors", "archive-impact", vendorId] as const,
  // SP-4 WS-1 — structured contract terms.
  vendorContractTerms: (vendorId: string) =>
    ["portfolio-admin", "vendors", "contract-terms", vendorId] as const,
  // Step 6 W5 — terms proposed from a countersigned schedule.
  vendorTermProposals: (vendorId: string) =>
    ["portfolio-admin", "vendors", "term-proposals", vendorId] as const,
  enforcerList: (params?: LocalEnforcerSearchParams) =>
    [
      "portfolio-admin",
      "local-enforcers",
      "list",
      params?.search ?? null,
      (params?.types ?? []).join(",") || null,
      (params?.statuses ?? []).join(",") || null,
      (params?.jurisdictions ?? []).join(",") || null,
    ] as const,
  enforcer: (enforcerId: string) =>
    ["portfolio-admin", "local-enforcers", "detail", enforcerId] as const,
  enforcerDetail: (enforcerId: string) =>
    [
      "portfolio-admin",
      "local-enforcers",
      "detail-rich",
      enforcerId,
    ] as const,
  enforcerEngagements: (enforcerId: string) =>
    [
      "portfolio-admin",
      "local-enforcers",
      "engagements",
      enforcerId,
    ] as const,
};

// ============================== Vendors ===============================

export function useVendorsList(params?: VendorSearchParams) {
  return useQuery<VendorDto[]>({
    queryKey: registryKeys.vendorList(params),
    queryFn: () => vendorsApi.search(params),
  });
}

export function useVendor(vendorId: string | undefined) {
  return useQuery<VendorDto>({
    queryKey: registryKeys.vendor(vendorId ?? ""),
    queryFn: () => vendorsApi.get(vendorId!),
    enabled: !!vendorId,
  });
}

export function useCreateVendor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateVendorRequest) => vendorsApi.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["portfolio-admin", "vendors"] });
    },
  });
}

export function useUpdateVendorProfile(vendorId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateVendorProfileRequest) =>
      vendorsApi.updateProfile(vendorId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: registryKeys.vendor(vendorId) });
      qc.invalidateQueries({
        queryKey: ["portfolio-admin", "vendors", "list"],
      });
    },
  });
}

// SP-4 WS-1 — structured contract terms (effective-dated).

export function useVendorContractTerms(vendorId: string | undefined) {
  return useQuery<VendorContractTermDto[]>({
    queryKey: registryKeys.vendorContractTerms(vendorId ?? ""),
    queryFn: () => vendorsApi.contractTerms(vendorId!),
    enabled: !!vendorId,
  });
}

export function useAddVendorContractTerm(vendorId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateVendorContractTermRequest) =>
      vendorsApi.addContractTerm(vendorId, body),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: registryKeys.vendorContractTerms(vendorId),
      });
      // Draft invoices carry a live contract check computed from these
      // terms — refresh the accounting reads so a no-terms draft upgrades.
      qc.invalidateQueries({ queryKey: vendorBillingKeys.all });
    },
  });
}

// Step 6 W5 — the bridge's review side.

export function useVendorTermProposals(vendorId: string | undefined) {
  return useQuery<VendorContractTermProposalDto[]>({
    queryKey: registryKeys.vendorTermProposals(vendorId ?? ""),
    queryFn: () => vendorsApi.termProposals(vendorId!),
    enabled: !!vendorId,
  });
}

/** Confirming creates a real term through the ordinary create path, so both
 *  the terms list and every draft invoice's live contract check change. */
export function useConfirmVendorTermProposal(vendorId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      proposalId,
      body,
    }: {
      proposalId: string;
      body: ConfirmVendorContractTermProposalRequest;
    }) => vendorsApi.confirmTermProposal(vendorId, proposalId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: registryKeys.vendorTermProposals(vendorId) });
      qc.invalidateQueries({ queryKey: registryKeys.vendorContractTerms(vendorId) });
      qc.invalidateQueries({ queryKey: vendorBillingKeys.all });
    },
  });
}

export function useDeclineVendorTermProposal(vendorId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ proposalId, reason }: { proposalId: string; reason: string }) =>
      vendorsApi.declineTermProposal(vendorId, proposalId, { reason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: registryKeys.vendorTermProposals(vendorId) });
    },
  });
}

export function useVendorAssignments(vendorId: string | undefined) {
  return useQuery<VendorLoanAssignmentDto[]>({
    queryKey: registryKeys.vendorAssignments(vendorId ?? ""),
    queryFn: () => vendorsApi.assignments(vendorId!),
    enabled: !!vendorId,
  });
}

export function useVendorPortalAccount(vendorId: string | undefined) {
  return useQuery<VendorPortalAccountDto | null>({
    queryKey: registryKeys.vendorPortalAccount(vendorId ?? ""),
    queryFn: () => vendorsApi.portalAccount(vendorId!),
    enabled: !!vendorId,
  });
}

export function useInviteVendorPortal(vendorId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => vendorsApi.invitePortal(vendorId),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: registryKeys.vendorPortalAccount(vendorId),
      });
    },
  });
}

export function useUpdateVendorStatus(vendorId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { toStatus: string; reason?: string | null }) =>
      vendorsApi.updateStatus(vendorId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: registryKeys.vendor(vendorId) });
      qc.invalidateQueries({
        queryKey: ["portfolio-admin", "vendors", "list"],
      });
    },
  });
}

// ---- archive / unarchive (F1) ----------------------------------------

export function useVendorArchiveImpact(vendorId: string | undefined) {
  return useQuery<VendorArchiveImpactDto>({
    queryKey: registryKeys.vendorArchiveImpact(vendorId ?? ""),
    queryFn: () => vendorsApi.archiveImpact(vendorId!),
    enabled: !!vendorId,
  });
}

export function useArchiveVendor(vendorId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ArchiveVendorRequest) =>
      vendorsApi.archive(vendorId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: registryKeys.vendor(vendorId) });
      qc.invalidateQueries({ queryKey: registryKeys.vendorsPrefix });
    },
  });
}

export function useUnarchiveVendor(vendorId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ArchiveVendorRequest) =>
      vendorsApi.unarchive(vendorId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: registryKeys.vendor(vendorId) });
      qc.invalidateQueries({ queryKey: registryKeys.vendorsPrefix });
    },
  });
}

// ============================== Local Enforcers =======================

export function useLocalEnforcersList(params?: LocalEnforcerSearchParams) {
  return useQuery<LocalEnforcerDto[]>({
    queryKey: registryKeys.enforcerList(params),
    queryFn: () => localEnforcersApi.search(params),
  });
}

export function useLocalEnforcer(enforcerId: string | undefined) {
  return useQuery<LocalEnforcerDto>({
    queryKey: registryKeys.enforcer(enforcerId ?? ""),
    queryFn: () => localEnforcersApi.get(enforcerId!),
    enabled: !!enforcerId,
  });
}

export function useLocalEnforcerDetail(enforcerId: string | undefined) {
  return useQuery<LocalEnforcerDetailDto>({
    queryKey: registryKeys.enforcerDetail(enforcerId ?? ""),
    queryFn: () => localEnforcersApi.getDetail(enforcerId!),
    enabled: !!enforcerId,
  });
}

export function useEnforcerEngagementsForEnforcer(
  enforcerId: string | undefined,
) {
  return useQuery<EnforcerEngagementDto[]>({
    queryKey: registryKeys.enforcerEngagements(enforcerId ?? ""),
    queryFn: () => enforcerEngagementsApi.listForEnforcer(enforcerId!),
    enabled: !!enforcerId,
  });
}

export function useCreateLocalEnforcer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateLocalEnforcerRequest) =>
      localEnforcersApi.create(body),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["portfolio-admin", "local-enforcers"],
      });
    },
  });
}

export function useUpdateLocalEnforcerStatus(enforcerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateLocalEnforcerStatusRequest) =>
      localEnforcersApi.updateStatus(enforcerId, body),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["portfolio-admin", "local-enforcers"],
      });
    },
  });
}

export function useQualifyLocalEnforcer(enforcerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: QualifyLocalEnforcerRequest) =>
      localEnforcersApi.qualify(enforcerId, body),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["portfolio-admin", "local-enforcers"],
      });
    },
  });
}

export function useRecordLocalEnforcerPerformanceReview(enforcerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: LocalEnforcerPerformanceReviewRequest) =>
      localEnforcersApi.recordPerformanceReview(enforcerId, body),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["portfolio-admin", "local-enforcers"],
      });
    },
  });
}
